/**
 * DataParser
 * Parses JSON configuration data for signers
 */

import { i18n } from '../utils/i18n.js';

export class DataParser {
  /**
   * Parse signers from JSON configuration
   * Expects lrsalud.json format with signers array
   */
  static parseSigners(jsonData) {
    if (!jsonData || !jsonData.signers || !Array.isArray(jsonData.signers)) {
      return [];
    }

    return jsonData.signers.map(signer => ({
      signerId: signer.signerId || `signer-${Date.now()}`,
      label: signer.label || i18n.t('signers.defaultLabel'),
      isMandatory: signer.isMandatory || false,
      isSigned: signer.isSigned || false,
      timestamp: signer.timeStamp || null,
      signatureType: signer.signatureType || 'dtu',
      signerListId: signer.signerListId || null,
      widget: signer.widget || null,
      ebpText: signer.ebpText || null,
      autoSign: signer.autoSign || false,
      img1B64: signer.img1B64 || null,
      img2B64: signer.img2B64 || null,
      // TSP configuration
      tspActivate: signer.tspActivate || false,
      tspUrl: signer.tspUrl || '',
      tspUser: signer.tspUser || '',
      tspPassword: signer.tspPassword || '',
      // OCSP configuration
      ocspActivate: signer.ocspActivate || false,
      ocspUrl: signer.ocspUrl || '',
      ocspUser: signer.ocspUser || '',
      ocspPassword: signer.ocspPassword || ''
    }));
  }

  /**
   * Parse settings from JSON configuration
   * Expects lrsalud.json format with settings object
   */
  static parseSettings(jsonData) {
    if (!jsonData || !jsonData.settings) {
      return {
        language: 'es',
        showViewPDFButton: false,
        showSignPaperButton: true,
        showFinishButton: true,
        showCancelButton: true,
        isLocked: false
      };
    }

    return {
      // UI settings
      language: jsonData.settings.language || 'es',
      showViewPDFButton: jsonData.settings.showViewPDFButton || false,
      showSignPaperButton: jsonData.settings.showSignPaperButton || false,
      showFinishButton: jsonData.settings.showFinishButton || false,
      showCancelButton: jsonData.settings.showCancelButton || false,
      isLocked: jsonData.settings.isLocked || false,
      
      // WebSign settings (can be at root or in settings)
      bearerToken: jsonData.settings.bearerToken,
      signatureEnvironment: jsonData.settings.signatureEnvironment || 'SANDBOX',
      
      // Preserve any other settings
      ...jsonData.settings
    };
  }

  /**
   * Parse signer lists (groups) from JSON configuration
   * Expects lrsalud.json format with signerLists array
   */
  static parseSignerLists(jsonData) {
    if (!jsonData || !jsonData.signerLists || !Array.isArray(jsonData.signerLists)) {
      return [];
    }

    return jsonData.signerLists.map(list => ({
      listId: list.listId,
      label: list.label,
      isExpanded: list.isExpanded !== false
    }));
  }

  /**
   * Parse integration data from JSON configuration
   */
  static parseIntegrationData(jsonData) {
    if (!jsonData || !jsonData.integrationData) {
      return {};
    }

    return jsonData.integrationData;
  }

  /**
   * Parse complete configuration
   * Expects lrsalud.json format
   */
  static parseConfiguration(jsonData) {
    return {
      signers: this.parseSigners(jsonData),
      signerLists: this.parseSignerLists(jsonData),
      settings: this.parseSettings(jsonData),
      integrationData: this.parseIntegrationData(jsonData)
    };
  }

  /**
   * Validate signer data
   */
  static validateSigner(signer) {
    const errors = [];

    if (!signer.signerId) {
      errors.push('signerId is required');
    }
    if (!signer.label) {
      errors.push('label is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate signers array
   */
  static validateSigners(signers) {
    if (!Array.isArray(signers)) {
      return {
        isValid: false,
        errors: ['signers must be an array']
      };
    }

    const allErrors = [];
    signers.forEach((signer, index) => {
      const validation = this.validateSigner(signer);
      if (!validation.isValid) {
        allErrors.push(`Signer ${index}: ${validation.errors.join(', ')}`);
      }
    });

    return {
      isValid: allErrors.length === 0,
      errors: allErrors
    };
  }
}
